import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { cn } from "@/lib/utils";
import { TrendingUp, TrendingDown, Minus, Activity, ChevronRight, Shield, Clock, AlertTriangle, CheckCircle, AlertCircle } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { RiskPulseWidget } from "./RiskPulseWidget";

interface RiskPulseSidebarProps {
  siteId?: string | null;
  companyId?: string | null;
  collapsed?: boolean;
}

interface PulseData {
  status: 'green' | 'amber' | 'red';
  score: number;
  trend: 'up' | 'down' | 'stable';
}

export const RiskPulseSidebar = ({ siteId, companyId, collapsed = false }: RiskPulseSidebarProps) => {
  const [pulse, setPulse] = useState<PulseData | null>(null);
  const [loading, setLoading] = useState(true);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [daysSinceIncident, setDaysSinceIncident] = useState<number | null>(null);

  useEffect(() => {
    fetchPulse();
    fetchLastIncident();
  }, [siteId, companyId]);

  const fetchPulse = async () => {
    try {
      const { data, error } = await supabase.functions.invoke('calculate-risk-pulse', {
        body: { siteId, companyId }
      });

      if (!error && data) {
        setPulse({
          status: data.status,
          score: data.score,
          trend: data.trend
        });
      }
    } catch (error) {
      console.error('Error fetching pulse:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchLastIncident = async () => {
    try {
      let query = supabase
        .from('peer_reports')
        .select('created_at')
        .in('risk_rating', ['high', 'very_high'])
        .order('created_at', { ascending: false })
        .limit(1);

      if (siteId) {
        query = query.eq('site_id', siteId);
      }

      const { data } = await query;
      if (data && data.length > 0) {
        const lastDate = new Date(data[0].created_at);
        const now = new Date();
        const diffTime = Math.abs(now.getTime() - lastDate.getTime());
        const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
        setDaysSinceIncident(diffDays);
      } else {
        setDaysSinceIncident(30);
      }
    } catch (error) {
      console.error('Error fetching last incident:', error);
    }
  };

  const getStatusConfig = (status: string) => {
    switch (status) {
      case 'green':
        return {
          bg: 'bg-gradient-to-br from-emerald-500 to-emerald-600',
          glow: 'shadow-emerald-500/40',
          label: 'Healthy',
          icon: CheckCircle,
          ringColor: 'ring-emerald-400/50'
        };
      case 'amber':
        return {
          bg: 'bg-gradient-to-br from-amber-500 to-orange-500',
          glow: 'shadow-amber-500/40',
          label: 'Caution',
          icon: AlertCircle,
          ringColor: 'ring-amber-400/50'
        };
      case 'red':
        return {
          bg: 'bg-gradient-to-br from-red-500 to-rose-600',
          glow: 'shadow-red-500/40',
          label: 'Alert',
          icon: AlertTriangle,
          ringColor: 'ring-red-400/50'
        };
      default:
        return {
          bg: 'bg-muted',
          glow: '',
          label: 'Unknown',
          icon: Activity,
          ringColor: 'ring-muted'
        };
    }
  };

  const TrendIcon = pulse?.trend === 'up' ? TrendingUp : pulse?.trend === 'down' ? TrendingDown : Minus;

  if (loading) {
    return (
      <div className={cn(
        "mx-3 mb-3 rounded-xl bg-secondary/30 animate-pulse",
        collapsed ? "h-12" : "h-32"
      )} />
    );
  }

  if (!pulse) return null;

  const config = getStatusConfig(pulse.status);
  const StatusIcon = config.icon;
  // Format score properly - multiply by 100 and round
  const scorePercent = Math.round(pulse.score * 100);

  // Collapsed state - just show status indicator
  if (collapsed) {
    return (
      <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
        <SheetTrigger asChild>
          <button className="mx-3 mb-3 p-3 rounded-xl bg-secondary/30 hover:bg-secondary/50 transition-all flex items-center justify-center group">
            <div className={cn(
              "w-4 h-4 rounded-full shadow-lg",
              config.bg,
              config.glow
            )}>
              <div className="w-full h-full rounded-full animate-ping opacity-40 bg-white" style={{ animationDuration: '2s' }} />
            </div>
          </button>
        </SheetTrigger>
        <SheetContent side="right" className="w-[500px] sm:w-[640px] p-0 overflow-y-auto">
          <SheetHeader className="p-6 border-b bg-gradient-to-r from-primary/5 to-transparent">
            <SheetTitle className="flex items-center gap-2">
              <Shield className="h-5 w-5 text-primary" />
              Organizational Risk Pulse™
            </SheetTitle>
          </SheetHeader>
          <div className="p-6">
            <RiskPulseWidget siteId={siteId} companyId={companyId} />
          </div>
        </SheetContent>
      </Sheet>
    );
  }

  // Expanded state - premium compact card
  return (
    <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
      <SheetTrigger asChild>
        <button className="mx-3 mb-3 rounded-xl overflow-hidden text-left group transition-all hover:shadow-lg">
          {/* Status header */}
          <div className={cn(
            "px-4 py-3 text-white relative",
            config.bg
          )}>
            {/* Subtle pulse animation */}
            <div className="absolute inset-0 overflow-hidden">
              <div className="absolute -right-4 -top-4 w-20 h-20 rounded-full bg-white/10 animate-pulse" style={{ animationDuration: '3s' }} />
            </div>
            
            <div className="relative flex items-center justify-between">
              <div className="flex items-center gap-2">
                <StatusIcon className="h-4 w-4" />
                <span className="text-xs font-semibold tracking-wide uppercase">Risk Pulse</span>
              </div>
              <ChevronRight className="h-4 w-4 opacity-60 group-hover:opacity-100 group-hover:translate-x-0.5 transition-all" />
            </div>
          </div>

          {/* Content */}
          <div className="bg-card border border-t-0 border-border/50 p-4 space-y-3">
            {/* Status + Score */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className={cn(
                  "w-2.5 h-2.5 rounded-full shadow",
                  config.bg
                )} />
                <span className="font-semibold text-sm">{config.label}</span>
              </div>
              <span className="text-xl font-bold tabular-nums">{scorePercent}%</span>
            </div>

            {/* Trend */}
            <div className="flex items-center gap-2">
              <TrendIcon className={cn(
                "h-3.5 w-3.5",
                pulse.trend === 'up' && "text-red-500",
                pulse.trend === 'down' && "text-emerald-500",
                pulse.trend === 'stable' && "text-muted-foreground"
              )} />
              <span className="text-xs text-muted-foreground capitalize">
                {pulse.trend === 'up' ? 'Trending Up' : pulse.trend === 'down' ? 'Trending Down' : 'Stable'}
              </span>
            </div>

            {/* Days since high risk */}
            {daysSinceIncident !== null && (
              <div className="flex items-center gap-2 pt-2 border-t border-border/50">
                <Clock className="h-3.5 w-3.5 text-muted-foreground" />
                <span className="text-xs text-muted-foreground">
                  <span className="font-semibold text-foreground">{daysSinceIncident}</span> days since high risk
                </span>
              </div>
            )}
          </div>
        </button>
      </SheetTrigger>
      <SheetContent side="right" className="w-[500px] sm:w-[640px] p-0 overflow-y-auto">
        <SheetHeader className="p-6 border-b bg-gradient-to-r from-primary/5 to-transparent">
          <SheetTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5 text-primary" />
            Organizational Risk Pulse™
          </SheetTitle>
        </SheetHeader>
        <div className="p-6">
          <RiskPulseWidget siteId={siteId} companyId={companyId} />
        </div>
      </SheetContent>
    </Sheet>
  );
};